import { useState, useEffect } from 'react'
import { apiFetch } from '../lib/apiFetch'

// ── Constants ──────────────────────────────────────────────────────
const CULTIVATION_TYPES = new Set(['flowering', 'veg', 'pre-veg'])

const STATUS_LABELS = {
  active:      'Active',
  ready:       'Ready',
  cleaning:    'Cleaning',
  maintenance: 'Maintenance',
  inactive:    'Inactive',
}

const STATUS_COLORS = {
  active:      '#4ade80',
  ready:       '#60a5fa',
  cleaning:    '#facc15',
  maintenance: '#fb923c',
  inactive:    'var(--text-dim)',
}

const ALL_STATUSES = ['all', 'active', 'ready', 'cleaning', 'maintenance', 'inactive']

// ── RoomManagementPage ─────────────────────────────────────────────
export default function RoomManagementPage({ onGoToHub }) {
  const [rooms, setRooms]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)
  const [filter, setFilter]         = useState('all')
  const [expanded, setExpanded]     = useState(null)
  // { [roomId]: { room, activeBatches, equipment, tasks } }
  const [detailCache, setDetailCache] = useState({})

  useEffect(() => {
    apiFetch('/api/lifechart/rooms/management')
      .then(data => { setRooms(data); setLoading(false) })
      .catch(err  => { setError(err.message ?? 'Failed to load rooms'); setLoading(false) })
  }, [])

  const filteredRooms  = filter === 'all' ? rooms : rooms.filter(r => r.status === filter)
  const cultivation    = filteredRooms.filter(r => CULTIVATION_TYPES.has(r.room_type))
  const postProduction = filteredRooms.filter(r => !CULTIVATION_TYPES.has(r.room_type))

  const handleExpand = async (roomId) => {
    if (expanded === roomId) { setExpanded(null); return }
    setExpanded(roomId)
    if (detailCache[roomId]) return
    setDetailCache(prev => ({ ...prev, [roomId]: 'loading' }))

    const [detail, tasks] = await Promise.all([
      apiFetch(`/api/lifechart/rooms/${roomId}/detail`).catch(() => ({})),
      apiFetch(`/api/lifechart/tasks?lc_room_id=${roomId}`).catch(() => []),
    ])
    setDetailCache(prev => ({ ...prev, [roomId]: { ...detail, tasks } }))
  }

  if (loading) return (
    <div className="rmp-shell">
      <RmpHeader onGoToHub={onGoToHub} />
      <div className="rmp-loading">LOADING ROOMS…</div>
    </div>
  )

  if (error) return (
    <div className="rmp-shell">
      <RmpHeader onGoToHub={onGoToHub} />
      <div className="rmp-error">{error}</div>
    </div>
  )

  return (
    <div className="rmp-shell">
      <RmpHeader onGoToHub={onGoToHub} />

      <div className="rmp-filter-bar" role="group" aria-label="Filter rooms by status">
        {ALL_STATUSES.map(s => (
          <button
            key={s}
            className={`rmp-filter-btn${filter === s ? ' rmp-filter-btn--active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'All' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <div className="rmp-content">
        <RoomSection
          label="CULTIVATION"
          rooms={cultivation}
          filter={filter}
          expanded={expanded}
          detailCache={detailCache}
          onExpand={handleExpand}
        />
        <RoomSection
          label="POST-PRODUCTION"
          rooms={postProduction}
          filter={filter}
          expanded={expanded}
          detailCache={detailCache}
          onExpand={handleExpand}
        />
      </div>
    </div>
  )
}

// ── RmpHeader ──────────────────────────────────────────────────────
function RmpHeader({ onGoToHub }) {
  return (
    <header className="rmp-header">
      <button className="rmp-back-btn" onClick={onGoToHub} aria-label="Return to hub">
        ← Hub
      </button>
      <h1 className="rmp-title">Room Management</h1>
    </header>
  )
}

// ── RoomSection ────────────────────────────────────────────────────
function RoomSection({ label, rooms, filter, expanded, detailCache, onExpand }) {
  // Hide empty sections only when a filter is active
  if (rooms.length === 0 && filter !== 'all') return null
  return (
    <section className="rmp-section">
      <h2 className="rmp-section-label">{label}</h2>
      {rooms.length === 0 ? (
        <p className="rmp-empty">No rooms match this filter.</p>
      ) : (
        <div className="rmp-grid">
          {rooms.map(room => (
            <RoomCard
              key={room.id}
              room={room}
              isExpanded={expanded === room.id}
              detail={detailCache[room.id]}
              onExpand={onExpand}
            />
          ))}
        </div>
      )}
    </section>
  )
}

// ── RoomCard ───────────────────────────────────────────────────────
function RoomCard({ room, isExpanded, detail, onExpand }) {
  const borderColor   = room.color ?? 'var(--accent)'
  const isCultivation = CULTIVATION_TYPES.has(room.room_type)

  const firstBatch      = room.active_batches?.[0]
  const extraBatchCount = (room.active_batches?.length ?? 0) - 1
  let batchLine = '—'
  if (firstBatch) {
    batchLine = `${firstBatch.strain_name} · ${firstBatch.schedule_name}`
    if (extraBatchCount > 0) batchLine += ` +${extraBatchCount} more`
  }

  const fillPct = isCultivation && room.max_plant_capacity > 0
    ? Math.min(100, Math.round((room.current_plant_count / room.max_plant_capacity) * 100))
    : null

  return (
    <div
      className={`rmp-card${isExpanded ? ' rmp-card--expanded' : ''}`}
      style={{ '--room-color': borderColor }}
      onClick={() => onExpand(room.id)}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onExpand(room.id)
        }
      }}
    >
      <div className="rmp-card-header">
        <div className="rmp-card-name-row">
          <span className="rmp-card-name">{room.name}</span>
          {room.room_code && <span className="rmp-card-code">· {room.room_code}</span>}
          <span
            className="rmp-status-badge"
            style={{ '--status-color': STATUS_COLORS[room.status] ?? 'var(--text-dim)' }}
          >
            {STATUS_LABELS[room.status] ?? room.status}
          </span>
        </div>

        {fillPct !== null && (
          <div className="rmp-fill-bar-wrap" aria-label={`${fillPct}% plant capacity`}>
            <div className="rmp-fill-bar">
              <div className="rmp-fill-bar-inner" style={{ width: `${fillPct}%` }} />
            </div>
            <span className="rmp-fill-pct">{fillPct}%</span>
            <span className="rmp-fill-count">
              {room.current_plant_count} / {room.max_plant_capacity} plants
            </span>
          </div>
        )}

        <div className="rmp-card-meta">
          <span className="rmp-batch-line">{batchLine}</span>
          {room.equipment_count > 0 && (
            <span className="rmp-equip-count">{room.equipment_count} devices</span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="rmp-expand-panel" onClick={e => e.stopPropagation()}>
          {!detail || detail === 'loading' ? (
            <div className="rmp-expand-loading">LOADING…</div>
          ) : (
            <ExpandDetail detail={detail} />
          )}
        </div>
      )}
    </div>
  )
}

// ── ExpandDetail ───────────────────────────────────────────────────
function ExpandDetail({ detail }) {
  const [showAllEquip, setShowAllEquip] = useState(false)
  const [showAllTasks, setShowAllTasks] = useState(false)

  const batches   = detail.activeBatches ?? []
  const equipment = detail.equipment     ?? []
  const tasks     = detail.tasks         ?? []

  const visibleEquip = showAllEquip ? equipment : equipment.slice(0, 10)
  const visibleTasks = showAllTasks ? tasks      : tasks.slice(0, 5)

  return (
    <div className="rmp-detail">

      {/* ── Batch ── */}
      <div className="rmp-detail-section">
        <h3 className="rmp-detail-label">Batch</h3>
        {batches.length === 0 ? (
          <p className="rmp-detail-empty">No active batch</p>
        ) : batches.map((b, i) => (
          <div key={b.id ?? i} className="rmp-batch-row">
            <span className="rmp-batch-strain">{b.strain_name}</span>
            <span className="rmp-batch-code">{b.batch_code}</span>
            <span className="rmp-batch-meta">
              {b.plant_count} plants · {b.schedule_name} · {b.total_grow_days}d ·{' '}
              {b.actual_start_date
                ? new Date(b.actual_start_date).toLocaleDateString('en-CA')
                : '—'}
            </span>
          </div>
        ))}
      </div>

      {/* ── Equipment ── */}
      <div className="rmp-detail-section">
        <h3 className="rmp-detail-label">Equipment</h3>
        {equipment.length === 0 ? (
          <p className="rmp-detail-empty">No equipment recorded</p>
        ) : (
          <>
            <table className="rmp-equip-table">
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Type</th>
                  <th>Serial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleEquip.map((e, i) => (
                  <tr key={e.id ?? i}>
                    <td>{e.name ?? e.device_name ?? '—'}</td>
                    <td>{e.type ?? e.device_type ?? '—'}</td>
                    <td className="rmp-serial">{e.serial_number ?? '—'}</td>
                    <td>
                      <span className={`rmp-equip-status rmp-equip-status--${e.status}`}>
                        {e.status === 'active'
                          ? '● Active'
                          : e.status === 'maintenance'
                          ? '● Maintenance'
                          : (e.status ?? '—')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!showAllEquip && equipment.length > 10 && (
              <button className="rmp-show-more" onClick={() => setShowAllEquip(true)}>
                Show all {equipment.length} devices
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Tasks ── */}
      {/* Note: the existing /tasks worker route already filters to !task.completed */}
      <div className="rmp-detail-section">
        <h3 className="rmp-detail-label">Tasks</h3>
        {tasks.length === 0 ? (
          <p className="rmp-detail-empty">No upcoming tasks</p>
        ) : (
          <>
            <ul className="rmp-tasks-list">
              {visibleTasks.map((t, i) => (
                <li key={t.id ?? i} className="rmp-task-item">
                  <span className="rmp-task-day">Day {t.day_number}</span>
                  <span className="rmp-task-title">{t.title ?? t.name ?? '—'}</span>
                </li>
              ))}
            </ul>
            {!showAllTasks && tasks.length > 5 && (
              <button className="rmp-show-more" onClick={() => setShowAllTasks(true)}>
                Show all {tasks.length} tasks
              </button>
            )}
          </>
        )}
      </div>

    </div>
  )
}
